import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, XCircle, Clock, FileText, AlertCircle,
  Eye, Download, ExternalLink, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';

interface PendingDocument {
  documentId: string;
  userId: string;
  fullName: string | null;
  documentType: string;
  originalFileName: string;
  contentType: string | null;
  fileSizeBytes: number | null;
  expiryDate: string | null;
  uploadedAt: string;
  reviewStatus: string;
  reviewNotes: string | null;

  /**
   * Set for vehicle-scoped documents. MOT certificates only reach this queue when DVSA holds
   * no record for the vehicle, and approving one verifies that single vehicle - so the
   * reviewer needs to see which plate they are vouching for.
   */
  vehicleId: string | null;
  vehicleRegistration: string | null;
}

interface DocumentDetail {
  document: PendingDocument;
  sasUrl: string | null;
}

const DOC_LABELS: Record<string, string> = {
  HireRewardInsurance:      'H&R Insurance',
  GoodsInTransitInsurance:  'GIT Insurance',
  PublicLiabilityInsurance: 'Public Liability',
  MotCertificate:           'MOT Certificate',
  DrivingLicence:           'Driving Licence',
  IdentityDocument:         'Identity Document',
};

const IMAGE_TYPES = new Set(['DrivingLicence', 'MotCertificate', 'IdentityDocument']);

function isImageDoc(doc: PendingDocument) {
  return IMAGE_TYPES.has(doc.documentType) || doc.contentType?.startsWith('image/') === true;
}

function fmtSize(bytes: number | null) {
  if (!bytes) return '—';
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'Approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle className="w-3 h-3" />Approved
    </span>
  );
  if (status === 'Rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      <XCircle className="w-3 h-3" />Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
      <Clock className="w-3 h-3" />Pending
    </span>
  );
}

function PreviewModal({ doc, onClose }: { doc: PendingDocument; onClose: () => void }) {
  const { getToken } = useAppAuth();
  const client = createApiClient(getToken);

  const { data, isLoading, isError } = useQuery<DocumentDetail>({
    queryKey: ['doc-detail', doc.documentId],
    queryFn: () =>
      client.get<DocumentDetail>(`/api/v1/documents/admin/${doc.documentId}`).then(r => r.data),
    staleTime: 25 * 60 * 1000,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900 text-sm">
              {DOC_LABELS[doc.documentType] ?? doc.documentType}
              {doc.vehicleRegistration && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium uppercase tracking-wide">
                  {doc.vehicleRegistration}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{doc.originalFileName}</p>
            {doc.vehicleRegistration && (
              <p className="text-xs text-amber-700 mt-1">
                The DVSA holds no MOT record for this vehicle. Approving verifies only {doc.vehicleRegistration}.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {data?.sasUrl && (
              <>
                <a
                  href={data.sasUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 px-3 py-1.5 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />Open
                </a>
                <a
                  href={data.sasUrl}
                  download={doc.originalFileName}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-800 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />Download
                </a>
              </>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          {isLoading && (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
            </div>
          )}
          {isError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <AlertCircle className="w-4 h-4 shrink-0" />Failed to load document preview.
            </div>
          )}
          {data && !isLoading && (
            data.sasUrl ? (
              isImageDoc(doc) ? (
                <img
                  src={data.sasUrl}
                  alt={DOC_LABELS[doc.documentType] ?? 'Document'}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 object-contain max-h-[60vh]"
                />
              ) : (
                <div className="text-center py-10 space-y-3">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto" />
                  <p className="text-sm text-gray-500">PDF — use the Open or Download buttons above to view.</p>
                </div>
              )
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <AlertCircle className="w-4 h-4 shrink-0" />Preview URL unavailable.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function RejectPanel({
  docId,
  onConfirm,
  onCancel,
  isPending,
}: {
  docId: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <tr>
      <td colSpan={8} className="px-4 py-3 bg-red-50 border-t border-red-100">
        <div className="flex items-start gap-3">
          <div className="flex-1 space-y-2">
            <label className="block text-xs font-medium text-red-700">
              Rejection reason <span className="font-normal text-red-500">(shown to driver)</span>
            </label>
            <textarea
              rows={2}
              placeholder="e.g. Document is expired, unreadable, or the wrong type."
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="w-full border border-red-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-400 resize-none bg-white"
            />
          </div>
          <div className="flex flex-col gap-2 shrink-0 pt-4">
            <button
              onClick={() => { if (!reason.trim()) return; onConfirm(reason.trim()); }}
              disabled={!reason.trim() || isPending}
              className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              {isPending
                ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <XCircle className="w-3.5 h-3.5" />
              }
              Confirm Reject
            </button>
            <button
              onClick={onCancel}
              className="text-xs text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AdminComplianceDocumentsPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();
  const client = createApiClient(getToken);

  const [page, setPage]           = useState(1);
  const [previewDoc, setPreview]  = useState<PendingDocument | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const { data: docs = [], isLoading, refetch } = useQuery<PendingDocument[]>({
    queryKey: ['admin-documents', page],
    queryFn: () =>
      client.get<PendingDocument[]>('/api/v1/documents/admin/pending', {
        params: { page, pageSize: 20 },
      }).then(r => r.data),
    enabled: !!account,
  });

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetch);

  const approveMut = useMutation({
    mutationFn: (docId: string) => client.post(`/api/v1/documents/admin/${docId}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-documents'] }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ docId, reason }: { docId: string; reason: string }) =>
      client.post(`/api/v1/documents/admin/${docId}/reject`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-documents'] });
      setRejectingId(null);
    },
  });

  const approving = approveMut.isPending ? (approveMut.variables as string) : null;

  return (
    <>
      {previewDoc && <PreviewModal doc={previewDoc} onClose={() => setPreview(null)} />}

      <div className="page-shell">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Compliance Document Review</h1>
            <p className="mt-1 text-sm text-gray-500">
              Review, open, and approve or reject compliance documents submitted by drivers and workshops.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {docs.filter(d => d.reviewStatus === 'Pending').length} pending
            </span>
            <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No pending documents</p>
            <p className="text-xs mt-1">All submitted documents have been reviewed.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">File</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Uploaded</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map(doc => {
                    const isPending     = doc.reviewStatus === 'Pending';
                    const isApproving   = approving === doc.documentId;
                    const isRejecting   = rejectMut.isPending && rejectingId === doc.documentId;
                    const showRejectRow = rejectingId === doc.documentId;

                    return (
                      <>
                        <tr
                          key={doc.documentId}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          {/* Type */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="font-medium text-gray-900 text-xs">
                                {DOC_LABELS[doc.documentType] ?? doc.documentType}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400 ml-5">
                              {isImageDoc(doc) ? 'Image' : 'PDF'}
                            </span>
                          </td>

                          {/* File */}
                          <td className="px-4 py-3 max-w-[200px]">
                            <p className="text-xs text-gray-800 font-medium truncate">{doc.originalFileName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <button
                                onClick={() => setPreview(doc)}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                <Eye className="w-3 h-3" />View
                              </button>
                              <span className="text-gray-300">·</span>
                              <ViewSasLink docId={doc.documentId} fileName={doc.originalFileName} getToken={getToken} />
                            </div>
                          </td>

                          {/* User */}
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-800 font-medium" title={doc.userId}>
                              {doc.fullName?.trim() || `${doc.userId.slice(0, 8)}…`}
                            </span>
                          </td>

                          {/* Uploaded */}
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {new Date(doc.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                            <span className="block text-gray-400">
                              {new Date(doc.uploadedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </td>

                          {/* Expires */}
                          <td className="px-4 py-3 whitespace-nowrap text-xs">
                            {doc.expiryDate ? (
                              (() => {
                                const days = Math.ceil((new Date(doc.expiryDate).getTime() - Date.now()) / 86_400_000);
                                return (
                                  <span className={days < 30 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                                    {new Date(doc.expiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {days < 30 && <span className="block text-red-400">{days < 0 ? 'Expired' : `${days}d`}</span>}
                                  </span>
                                );
                              })()
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Size */}
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {fmtSize(doc.fileSizeBytes)}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={doc.reviewStatus} />
                            {doc.reviewStatus === 'Rejected' && doc.reviewNotes && (
                              <p className="text-xs text-red-400 mt-0.5 max-w-[160px] truncate" title={doc.reviewNotes}>
                                {doc.reviewNotes}
                              </p>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isPending ? (
                              <div className="flex items-center gap-2">
                                <button
                                  title="Approve"
                                  onClick={() => approveMut.mutate(doc.documentId)}
                                  disabled={isApproving || isRejecting}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg transition-colors"
                                >
                                  {isApproving
                                    ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <CheckCircle className="w-3.5 h-3.5" />
                                  }
                                  Approve
                                </button>
                                <button
                                  onClick={() => setRejectingId(showRejectRow ? null : doc.documentId)}
                                  disabled={isApproving}
                                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                    showRejectRow
                                      ? 'bg-red-100 text-red-700 border border-red-200'
                                      : 'bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white'
                                  }`}
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                  Reject
                                  {showRejectRow
                                    ? <ChevronUp className="w-3 h-3 ml-0.5" />
                                    : <ChevronDown className="w-3 h-3 ml-0.5" />
                                  }
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>

                        {/* Inline reject panel */}
                        {showRejectRow && (
                          <RejectPanel
                            key={`reject-${doc.documentId}`}
                            docId={doc.documentId}
                            isPending={isRejecting}
                            onConfirm={reason => rejectMut.mutate({ docId: doc.documentId, reason })}
                            onCancel={() => setRejectingId(null)}
                          />
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && (docs.length === 20 || page > 1) && (
          <div className="flex justify-center gap-3 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500 flex items-center">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={docs.length < 20}
              className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// Lazily fetches the SAS URL and renders an open + download link pair.
function ViewSasLink({
  docId,
  fileName,
  getToken,
}: {
  docId: string;
  fileName: string;
  getToken: () => Promise<string>;
}) {
  const [fetching, setFetching] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const client = createApiClient(getToken);

  const load = async () => {
    if (url || fetching) return;
    setFetching(true);
    try {
      const r = await client.get<{ document: PendingDocument; sasUrl: string | null }>(
        `/api/v1/documents/admin/${docId}`
      );
      setUrl(r.data.sasUrl ?? null);
    } catch {
      setErr(true);
    } finally {
      setFetching(false);
    }
  };

  if (err) return <span className="text-xs text-red-400">Error</span>;
  if (fetching) return <span className="text-xs text-gray-400 animate-pulse">Loading…</span>;

  if (url) {
    return (
      <div className="flex items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline">
          <ExternalLink className="w-3 h-3" />Open
        </a>
        <span className="text-gray-300">·</span>
        <a href={url} download={fileName}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:underline">
          <Download className="w-3 h-3" />Download
        </a>
      </div>
    );
  }

  return (
    <button onClick={load}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:underline">
      <Download className="w-3 h-3" />Download
    </button>
  );
}
